import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtMoneyK } from './UI';

// Branch Budget widget. Mirrors the controller's weekly report layout:
//   Base | Stronghold | Turnaround | TOTAL | Accrual | GRAND TOTAL | Forecast | Balance
// FR was dropped 2026-05-13 -- no longer tracked.
// Reps see their branch's numbers alongside their personal Performance
// Targets. Two scopes: This Month and YTD.

export default function BranchBudgetWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.myBranchBudget(year).then(setData).catch(e => setError(e.message));
  }, [year]);

  const summary = useMemo(() => {
    if (!data) return null;
    // For non-TA-DIV single-branch views, TA is a VISUAL CREDIT only --
    // not summed into Total / Forecast / Balance. The widget still shows
    // the TA cell (italic gray) but it doesn't influence the math.
    const includeTa = !!data.is_ta_div;
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const sum = (months) => months.reduce((a, m) => ({
      base:       a.base       + Number(m.base_actual),
      stronghold: a.stronghold + Number(m.stronghold_actual),
      ta:         a.ta         + Number(m.ta_actual),
      accrual:    a.accrual    + Number(m.accrual),
      forecast:   a.forecast   + Number(m.base_forecast) + (includeTa ? Number(m.ta_forecast) : 0),
      budget:     a.budget     + Number(m.base_budget)   + (includeTa ? Number(m.ta_budget) : 0)
    }), { base: 0, stronghold: 0, ta: 0, accrual: 0, forecast: 0, budget: 0 });
    const decorate = (s) => {
      const total = s.base + s.stronghold + (includeTa ? s.ta : 0);
      const grand = total + s.accrual;
      // Variance sign convention -- positive = beat target, negative = missed.
      // vs Budget    = accountability number (original commitment).
      // vs Forecast  = operational pacing (against revised expectation).
      const vsBudget   = grand - s.budget;
      const vsForecast = grand - s.forecast;
      return { ...s, total, grand, vsBudget, vsForecast };
    };
    // "Has activity" predicate -- a month counts toward YTD only once it
    // has billed revenue (or a manual stronghold / accrual entry). This
    // matches the rule George set: don't include the current month in
    // YTD targets until something has been recorded for it, otherwise the
    // current month's full Budget / Forecast skews the YTD comparison
    // before there's anything to compare against. Once any data lands
    // for the month, the full month flips into YTD so the user can see
    // "what's still needed to hit the month's target."
    const hasMonthActivity = (m) => {
      const grand = Number(m.base_actual) + Number(m.stronghold_actual) +
                    (includeTa ? Number(m.ta_actual) : 0) + Number(m.accrual);
      return grand !== 0;
    };
    const ytdMonths = data.months.filter(hasMonthActivity);
    // Label the YTD row with the LAST billed month, not the current
    // calendar month. So mid-May before May uploads: "YTD (Apr)".
    const lastBilledIdx = ytdMonths.length
      ? data.months.indexOf(ytdMonths[ytdMonths.length - 1])
      : currentMonth - 1;
    const ytdLabel = ytdMonths.length
      ? new Date(year, lastBilledIdx, 1).toLocaleDateString('en-US', { month: 'long' })
      : '—';
    const monthName = new Date(year, currentMonth - 1, 1).toLocaleDateString('en-US', { month: 'long' });
    // Per-month decorated rows for the expanded view. Each entry is one
    // calendar month with its own actuals / forecast / balance computed
    // exactly the same way as the YTD summary row -- just over a single
    // month instead of a slice.
    const perMonth = data.months.map((m, i) => ({
      ...decorate(sum([m])),
      month: i + 1,
      name: new Date(year, i, 1).toLocaleDateString('en-US', { month: 'short' })
    }));
    return {
      monthName,
      currentMonth,
      ytdLabel,
      thisMonth: decorate(sum([data.months[currentMonth - 1]])),
      ytd: decorate(sum(ytdMonths)),
      annual: decorate(sum(data.months)),
      perMonth
    };
  }, [data, year]);

  if (error) return <div style={styles.errorBox}>{error}</div>;
  if (!data) return null;

  const hasAnyData = data.months.some(m =>
    m.base_budget > 0 || m.ta_budget > 0 || m.base_forecast > 0 || m.ta_forecast > 0 ||
    m.base_actual > 0 || m.stronghold_actual > 0 || m.ta_actual > 0
  );
  if (!hasAnyData) {
    return (
      <div style={styles.card}>
        <h2 style={styles.h2}>
          {data.is_rollup ? '🏢 Corporate Roll-Up' : `🏢 Branch Budget · ${data.branch_name}`}
          <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {year}</span>
        </h2>
        <p style={{ color: colors.textMuted, margin: 0, fontSize: 13 }}>
          No data for {year} yet. Admin enters monthly Budget + Forecast under
          {' '}<em>Admin → Branch Budgets</em>; actuals auto-populate from Sales Revenue + Project Tracker.
        </p>
      </div>
    );
  }

  const { monthName, currentMonth, ytdLabel, thisMonth, ytd, annual, perMonth } = summary;
  // Variance coloring: positive (Actual beat target) = green; within 10% under = amber;
  // worse = red. Target=0 (no budget/forecast set) OR no actuals yet renders muted '—'.
  const varColor = (v, target, hasActual) => (!hasActual || target === 0) ? colors.textMuted
                                : v >= 0 ? colors.green
                                : v >= -target * 0.1 ? colors.orange
                                : colors.red;
  // Formatted variance cell: '+$X' green if beat, '-$X' red if missed,
  // '—' when no target OR no actual yet (avoids showing huge red numbers
  // for future months that simply haven't billed yet).
  const fmtVar = (v, target, hasActual) => {
    if (!hasActual) return '—';
    if (target === 0) return '—';
    return (v >= 0 ? '+' : '-') + fmtMoneyK(Math.abs(v));
  };

  const taStyle = data.is_ta_div
    ? { ...styles.td, textAlign: 'right' }
    : { ...styles.td, textAlign: 'right', color: colors.textMuted, fontStyle: 'italic' };

  const Row = ({ label, s, highlight }) => {
    // hasActual: was any revenue billed for this row's period? Used to
    // gate the variance columns so future / not-yet-billed months show
    // '—' instead of misleading huge red numbers.
    const hasActual = (Number(s.grand) || 0) !== 0;
    return (
    <tr style={highlight ? { background: '#FFF7ED' } : undefined}>
      <td style={{ ...styles.td, fontWeight: 600 }}>{label}</td>
      <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoneyK(s.base)}</td>
      <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoneyK(s.stronghold)}</td>
      <td style={taStyle} title={data.is_ta_div ? '' : 'Visual credit only — not counted in this branch\'s Total. TA revenue books to TA-DIV-800.'}>
        {fmtMoneyK(s.ta)}{!data.is_ta_div && <span style={{ marginLeft: 3, fontSize: 9 }}>*</span>}
      </td>
      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoneyK(s.total)}</td>
      <td style={{ ...styles.td, textAlign: 'right', color: s.accrual < 0 ? colors.red : colors.textMuted }}>
        {s.accrual < 0 ? '-' : ''}{fmtMoneyK(Math.abs(s.accrual))}
      </td>
      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{fmtMoneyK(s.grand)}</td>
      <td style={{ ...styles.td, textAlign: 'right', color: colors.textMuted }}>{fmtMoneyK(s.budget)}</td>
      <td style={{ ...styles.td, textAlign: 'right', color: colors.textMuted }}>{fmtMoneyK(s.forecast)}</td>
      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: varColor(s.vsBudget, s.budget, hasActual) }}>
        {fmtVar(s.vsBudget, s.budget, hasActual)}
      </td>
      <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: varColor(s.vsForecast, s.forecast, hasActual) }}>
        {fmtVar(s.vsForecast, s.forecast, hasActual)}
      </td>
    </tr>
    );
  };

  // Tooltip text -- different message for the admin/leadership roll-up vs
  // a single branch's view. The roll-up is summed across all branches
  // with has_sales_budget=true (current operating branches, excluding
  // Corporate HQ which is org-only).
  const WIDGET_HELP = data.is_rollup
    ? `Company-wide totals across all ${data.branch_count} operating branches.\n` +
      `  Base       — every rep's Base sales (from the Rentalman upload)\n` +
      `  Stronghold — branch Stronghold revenue (entered manually)\n` +
      `  TA         — every rep's TA sales (books to TA-DIV-800)\n` +
      `  Accrual    — accounting adjustments\n` +
      `  Budget     — annual commitment set at start of year\n` +
      `  Forecast   — revised expectation (admin updates as year unfolds)\n` +
      `vs Budget   = Actual − Budget. Positive (green) = beat commitment, negative (red) = missed.\n` +
      `vs Forecast = Actual − Forecast. Positive = beat the revised target.\n` +
      `Per-branch detail in Admin → Branch Budgets.`
    : data.is_ta_div
      ? `Branch totals for ${data.branch_name}. For your individual ` +
        `numbers see Performance Targets above.\n` +
        `  Base       — Base sales billed at this branch\n` +
        `  Stronghold — branch Stronghold product line (entered manually)\n` +
        `  TA         — TA sales (08xx Loc) booked here\n` +
        `  Accrual    — accounting adjustment\n` +
        `  Budget     — annual commitment set at start of year\n` +
        `  Forecast   — revised expectation (admin updates as year unfolds)\n` +
        `vs Budget   = Actual − Budget. Positive (green) = beat commitment, negative (red) = missed.\n` +
        `vs Forecast = Actual − Forecast. Positive = beat the revised target.`
      : `Branch totals for ${data.branch_name}. For your individual ` +
        `numbers see Performance Targets above.\n` +
        `  Base       — Base sales billed at this branch\n` +
        `  Stronghold — branch Stronghold product line (entered manually)\n` +
        `  TA         — visual credit only. Shows this branch's reps' TA ` +
                       `contribution but does NOT count in Total. Actual TA ` +
                       `revenue books to TA-DIV-800.\n` +
        `  Accrual    — accounting adjustment\n` +
        `  Budget     — annual Base commitment set at start of year\n` +
        `  Forecast   — revised Base expectation (admin updates as year unfolds)\n` +
        `vs Budget   = Actual − Budget. Positive (green) = beat commitment, negative (red) = missed.\n` +
        `vs Forecast = Actual − Forecast. Positive = beat the revised target.`;

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          {data.is_rollup ? '🏢 Corporate Roll-Up' : `🏢 Branch Budget · ${data.branch_name}`}
          <span
            title={WIDGET_HELP}
            aria-label="What this widget shows"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: '50%',
              border: '1px solid #9CA3AF', color: '#6B7280',
              fontSize: 10, fontWeight: 700, cursor: 'help', marginLeft: 8,
              verticalAlign: 'middle'
            }}
          >?</span>
          <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {year}</span>
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: colors.textMuted }}>
            YTD vs Budget:{' '}
            <strong style={{ color: varColor(ytd.vsBudget, ytd.budget, ytd.grand !== 0) }}>
              {fmtVar(ytd.vsBudget, ytd.budget, ytd.grand !== 0)}
            </strong>
            <span style={{ marginLeft: 8 }}>
              · vs Forecast:{' '}
              <strong style={{ color: varColor(ytd.vsForecast, ytd.forecast, ytd.grand !== 0) }}>
                {fmtVar(ytd.vsForecast, ytd.forecast, ytd.grand !== 0)}
              </strong>
            </span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Hide month-by-month breakdown' : 'Show every month'}
            style={{
              fontSize: 11, fontWeight: 600, padding: '4px 10px',
              border: `1px solid ${colors.border}`, borderRadius: 6,
              background: '#fff', color: colors.charcoal, cursor: 'pointer'
            }}
          >
            {expanded ? '▲ Collapse' : '▼ View all months'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...styles.table, fontSize: 12, minWidth: 920 }}>
          <thead>
            <tr>
              <th style={styles.th}></th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Base</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Stronghold</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>TA</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>TOTAL</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Accrual</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>GRAND</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Budget</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Forecast</th>
              <th style={{ ...styles.th, textAlign: 'right' }} title="Actual − Budget. Positive = beat the original commitment.">vs Budget</th>
              <th style={{ ...styles.th, textAlign: 'right' }} title="Actual − Forecast. Positive = beat the revised expectation.">vs Forecast</th>
            </tr>
          </thead>
          <tbody>
            {expanded ? (
              // Expanded: show every month Jan -> Dec (future months render
              // their zeros plainly, so the user can see the calendar laid out).
              // The current month gets a subtle highlight so it doesn't get
              // lost in the list.
              perMonth.map(m => (
                <Row
                  key={m.month}
                  label={m.name}
                  s={m}
                  highlight={m.month === currentMonth}
                />
              ))
            ) : (
              <Row label={monthName} s={thisMonth} />
            )}
            <Row label={`YTD (${ytdLabel})`} s={ytd} />
            <tr style={{ background: '#F9FAFB', color: colors.textMuted }}>
              <td style={{ ...styles.td, fontWeight: 600 }}>Full Year</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoneyK(annual.budget)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoneyK(annual.forecast)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
